import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaxExemption, TaxRate } from '../models/api-models';
import { getTaxRates, getTaxExemptions, isTaxRatesLoading, isTaxExemptionsLoading } from '../store/settings/selectors';
import { loadTaxRates, loadTaxExemptions } from '../store/settings/actions';

export interface TaxDataHook {
    /** Tax rates loaded from the API */
    taxRates: TaxRate[];
    taxRatesMap: Record<string, TaxRate>;
    /** Tax exemptions loaded from the API */
    taxExemptions: TaxExemption[];
    /** True if tax data is currently being loaded */
    isLoading: boolean;
    /** True if tenant has invoice settings configured (has tax rates or exemptions) */
    isConfigured: boolean;
    /** Array of tax display names (with "IVA - " prefix removed) */
    taxOptions: string[];
    /** Map of exemption code to formatted display text (e.g., "M07" -> "M07 - Legal text...") */
    taxExemptionOptionsMap: Record<string, string>;
    /** Array of formatted exemption display texts */
    taxExemptionOptions: string[];
    /** Check if a given tax ID or name represents an exempt (0%) tax rate */
    isExemptTax: (taxIdOrName: string | number | undefined) => boolean;
    /** Get the first available tax exemption as default */
    getDefaultExemption: () => { id: string | undefined; law: string | undefined };
    /** Convert a tax ID to its display name (without "IVA - " prefix) */
    getTaxDisplayName: (taxId: string | number | undefined) => string;
    /** Convert a tax display name back to its ID */
    getTaxIdFromDisplayName: (displayName: string | undefined) => string | undefined;
    /** Convert a tax ID to its display name (legacy method, prefer getTaxDisplayName) */
    getTaxNameFromTaxId: (taxId: string | number | undefined, exemptionCode?: string) => string;
    getTaxExemptionIdFromDisplayName: (displayName: string) => string | undefined;
    getTaxExemptionNameFromTaxExemptionId: (examptionId: string | number | undefined) => string;
}

/**
 * Unified hook for accessing tax rates and exemptions data with helper utilities.
 *
 * Automatically loads tax rates and exemptions from the API if not already cached in Redux store.
 * Returns empty arrays if the tenant doesn't have invoice settings configured.
 * Provides helper functions for converting between tax IDs and display names, checking exempt status, etc.
 *
 * @returns {TaxDataHook} Object containing tax data arrays, loading state, and helper functions
 *
 * @example
 * ```tsx
 * const { taxRates, isExemptTax, getTaxDisplayName } = useTaxData();
 *
 * const displayName = getTaxDisplayName(item.taxId); // "Normal (23%)"
 * const isExempt = isExemptTax(item.taxId); // false
 * ```
 */
export const useTaxData = (): TaxDataHook => {
    const dispatch = useDispatch();
    const requestedTaxRatesRef = useRef(false);
    const requestedTaxExemptionsRef = useRef(false);

    const taxRates = useSelector(getTaxRates) || [];
    const taxExemptions = useSelector(getTaxExemptions) || [];
    const taxRatesLoading = useSelector(isTaxRatesLoading);
    const taxExemptionsLoading = useSelector(isTaxExemptionsLoading);

    useEffect(() => {
        if (!taxRates.length && !taxRatesLoading && !requestedTaxRatesRef.current) {
            requestedTaxRatesRef.current = true;
            dispatch(loadTaxRates());
        }
    }, [dispatch, taxRates.length, taxRatesLoading]);

    useEffect(() => {
        if (!taxExemptions.length && !taxExemptionsLoading && !requestedTaxExemptionsRef.current) {
            requestedTaxExemptionsRef.current = true;
            dispatch(loadTaxExemptions());
        }
    }, [dispatch, taxExemptions.length, taxExemptionsLoading]);

    const isLoading = taxRatesLoading || taxExemptionsLoading;
    const isConfigured = taxRates.length > 0 || taxExemptions.length > 0;

    const taxOptions = useMemo(() => {
        return taxRates.map(rate => rate.title?.replace(/^IVA\s*-\s*/i, '') || '').filter(Boolean);
    }, [taxRates]);
    
    const taxRatesMap = useMemo<Record<string, TaxRate>>(() => 
        Object.fromEntries(taxRates.map(rate => [rate.id, rate])), 
        [taxRates]);

    const taxExemptionOptionsMap = useMemo(() => {
        const map: Record<string, string> = {};
        taxExemptions.forEach(exemption => {
            if (exemption && exemption.id && exemption.text) {
                map[exemption.id] = exemption.text;
            }
        });
        return map;
    }, [taxExemptions]);

    const taxExemptionOptions = useMemo(() => {
        return Object.values(taxExemptionOptionsMap).filter(Boolean);
    }, [taxExemptionOptionsMap]);

    const isExemptTax = useCallback((taxIdOrName: string | number | undefined): boolean => {
        if (!taxIdOrName) return false;

        const matchingRate = taxRates.find(rate =>
            rate.id === String(taxIdOrName) ||
            rate.title?.replace(/^IVA\s*-\s*/i, '') === taxIdOrName
        );

        return matchingRate ? matchingRate.rate === 0 : false;
    }, [taxRates]);

    const getDefaultExemption = useCallback(() => {
        if (!taxExemptions.length) {
            return { id: undefined, law: undefined };
        }

        const firstExemption = taxExemptions[0];
        return {
            id: firstExemption.id,
            law: firstExemption.law?.[0],
        };
    }, [taxExemptions]);

    const getTaxDisplayName = useCallback((taxId: string | number | undefined): string => {
        if (taxId === undefined) return taxOptions[0] || '';

        const matchingRate = taxRates.find(rate => rate.id === String(taxId));
        return matchingRate ? (matchingRate.title?.replace(/^IVA\s*-\s*/i, '') || '') : taxOptions[0] || '';
    }, [taxRates, taxOptions]);

    const getTaxIdFromDisplayName = useCallback((displayName: string | undefined): string | undefined => {
        if (displayName === undefined) {
            return taxRates.length > 0 ? taxRates[0].id : undefined;
        }
        
        const matchingRate = taxRates.find(rate =>
            rate.title?.replace(/^IVA\s*-\s*/i, '') === displayName
        );
        return matchingRate?.id;
    }, [taxRates]);

    const getTaxNameFromTaxId = useCallback((taxId: string | number | undefined): string => {
        if (taxId === undefined) {
            return taxRates.length > 0 ? (taxRates[0].title?.replace(/^IVA\s*-\s*/i, '') || '') : '';
        }
        const matchingRate = taxRates.find(rate => rate.id === String(taxId));
        return matchingRate ? (matchingRate.title?.replace(/^IVA\s*-\s*/i, '') || '') : '';
    }, [taxRates]);

    const getTaxExemptionIdFromDisplayName = useCallback((displayName: string): string | undefined => {
        const matchingRate = taxExemptions.find(e => e.text === displayName);
        return matchingRate?.id;
    }, [taxExemptions]);

    const getTaxExemptionNameFromTaxExemptionId = useCallback((id: string | number | undefined): string => {
        if (id === undefined) {
            return taxExemptions.length > 0 ? (taxExemptions[0].text || '') : '';
        }
        const matchingRate = taxExemptions.find(rate => rate.id === String(id));
        return matchingRate ? (matchingRate.text?.replace(/^IVA\s*-\s*/i, '') || '') : '';
    }, [taxExemptions]);

    return {
        taxRates,
        taxRatesMap,
        taxExemptions,
        isLoading,
        isConfigured,
        taxOptions,
        taxExemptionOptionsMap,
        taxExemptionOptions,
        isExemptTax,
        getDefaultExemption,
        getTaxDisplayName,
        getTaxIdFromDisplayName,
        getTaxNameFromTaxId,
        getTaxExemptionIdFromDisplayName,
        getTaxExemptionNameFromTaxExemptionId,
    };
};
